import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Quote, ChevronRight } from "lucide-react";
import { getDirector, DIRECTORS } from "@/lib/directors";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { WhatsAppFab } from "@/components/site/WhatsAppFab";

export default function DirectorDetail() {
  const { slug } = useParams();
  const d = getDirector(slug);
  if (!d) return <Navigate to="/directors" replace />;

  const others = DIRECTORS.filter((o) => o.slug !== d.slug);

  return (
    <div className="min-h-screen bg-white" data-testid={`director-detail-${d.slug}`}>
      <Navbar onQuote={() => (window.location.href = "/#contact")} />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-16 relative overflow-hidden">
        <div className={`absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-gradient-to-br ${d.accent} opacity-15 blur-3xl pointer-events-none`} />
        <div className="absolute inset-0 vc-grid-bg pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-5 md:px-10">
          <Link
            to="/directors"
            data-testid="back-directors"
            className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-600 hover:text-neutral-900 transition-colors mb-8"
          >
            <ArrowLeft size={16} /> All Directors
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="font-mono-vc text-xs uppercase tracking-[0.25em] vc-text-gradient font-bold">
                Director&apos;s Message
              </div>
              <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-neutral-900">
                {d.name}
              </h1>
              <div className="mt-3 text-neutral-600 text-base md:text-lg">{d.title}</div>

              <div className="mt-7 flex flex-wrap gap-2">
                {d.focus.map((f) => (
                  <span key={f} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-neutral-100 text-neutral-700">
                    {f}
                  </span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2 relative">
              <div className={`absolute -inset-3 rounded-[2rem] bg-gradient-to-br ${d.accent} opacity-30 blur-2xl`} aria-hidden />
              <div className="relative rounded-3xl overflow-hidden border border-neutral-200 aspect-[4/3] shadow-2xl">
                <img src={d.photo} alt={d.name} className="absolute inset-0 w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Message */}
      <section className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-5 md:px-10">
          <div className="relative rounded-3xl border border-neutral-200 bg-white p-8 md:p-14">
            <Quote className={`text-orange-500 mb-6`} size={36} />
            <div className="space-y-6 font-display text-xl md:text-2xl leading-[1.55] text-neutral-800">
              {d.message.map((p, i) => (
                <p key={`${d.slug}-p-${i}`}>{p}</p>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-neutral-200">
              <div className="font-display text-2xl font-extrabold vc-text-gradient">— {d.name}</div>
              <div className="text-sm text-neutral-500 mt-1">{d.title}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Other directors */}
      <section className="py-16 md:py-24 bg-neutral-50/60 border-t border-neutral-200/70">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-neutral-900">
              Other Directors
            </h2>
            <Link
              to="/directors"
              className="text-sm font-semibold text-neutral-700 hover:text-neutral-900 inline-flex items-center gap-1"
              data-testid="view-all-directors"
            >
              View all <ChevronRight size={16} />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/directors/${o.slug}`}
                data-testid={`other-director-${o.slug}`}
                className="group flex items-center gap-5 rounded-2xl border border-neutral-200 bg-white p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <img src={o.photo} alt={o.name} className="w-20 h-20 rounded-2xl object-cover" />
                <div className="flex-1">
                  <div className="font-display text-lg font-bold text-neutral-900">{o.name}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">{o.title}</div>
                  <div className="text-sm text-neutral-600 mt-2">{o.short}</div>
                </div>
                <ChevronRight className="text-neutral-400 group-hover:text-neutral-900 transition-colors" size={18} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
